/* ─────────────────────────────────────────────────────────────
   pack3d — renders the card-pack GLTF in place of the CSS pack.

   Ported from the TradingCard build, with the same architecture and
   for the same reasons:

   One shared WebGLRenderer, one shared scene/mesh/camera. Each host
   element gets a plain 2D canvas; we render the model at that pack's
   size and blit the result in. A dozen packs on the shop grid would
   otherwise mean a dozen WebGL contexts (browsers cap out around 16)
   and a dozen copies of the environment map.

   Rendering is on demand. A pack that isn't moving is a static bitmap
   costing nothing; the rAF loop parks itself when no instance is dirty
   and wakes on interaction.

   The Foil material omits metallicFactor, which per the glTF spec means
   1.0 — fully metallic. Metals take nearly all their colour from
   reflections rather than direct light, so lighting this with lamps
   alone renders it near-black. The look comes from the prefiltered
   environment built in buildEnvironment().

   The difference from the original: instances are mounted explicitly by
   React rather than discovered with a MutationObserver. React already
   knows when a pack enters and leaves the tree, so a DOM scan would be
   a second, slower source of truth for something we are told directly.
   ───────────────────────────────────────────────────────────── */
import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

import { createCardBackCanvas } from './card-back'

const MODEL_URL = '/models/card_pack.gltf'

/* Where the UV layout puts flat colour. The islands sit in the upper
   right of the sheet, so anything down here is clear background — the
   sampling point for parts that carry no UVs of their own. */
const FLAT_UV: [number, number] = [0.05, 0.05]
const MAX_DPR = 2

/* How long a card takes to slide clear of the mouth. */
export const CARD_OUT_MS = 620

/* How long the lid takes to come off. The rip flow waits 720ms between
   the tear firing and the card reveal, so this has to land inside that. */
export const TEAR_OFF_MS = 680

/* A Blender Empty exports as a glTF node with a name and a translation
   but no `mesh`, which GLTFLoader turns into a plain Object3D. The model
   ships one called "tear", and it becomes the pack's tear handle. */
const TEAR_NAME = /^(tear|tab|rip|open)/i

/* The mesh that tears away ("pack_top"). Matched separately from
   TEAR_NAME so the Empty and the lid can't be confused. */
const LID_NAME = /(top|lid|cap|flap)/i

/* The card slab that slides out. Checked before LID_NAME so a name like
   "card_top" could never be mistaken for the lid. */
const CARD_NAME = /^card/i

/* Resting yaw: square to the camera, so a pack reads as a printed front
   rather than as an object left at whatever angle it was last spun to.

   Tearable packs are the exception. The tear Empty sits on the LEFT edge of
   the model at mid-depth, which at yaw 0 lands almost exactly on the
   silhouette — the facing test would hide the hotspot half the time and the
   tab would be a sliver to aim at. Those rest turned slightly so the tab
   swings toward the viewer and is reachable the moment the pack appears. */
const REST_YAW = 0
const TEAR_REST_YAW = 0.34

/* Yaw speed, in radians/second, below which a pack starts easing home. Above
   it the throw is still the interesting thing, and pulling the pack back mid
   spin reads as it being yanked rather than as it settling. */
const SETTLE_BELOW = 0.8

/* Share of the remaining angle still left after a full second of settling.
   Expressed as a retention rather than a per-frame step so the return takes
   the same wall-clock time at 60Hz and at 144Hz. */
const SETTLE_RETAIN = 0.0006

export interface FoilConfig {
  iridescence: number
  thickness: [number, number]
  rough: number
  clearcoat: number
  /** How far toward white the accent is pulled; foil is never a pure hue. */
  tint: number
  /** Overall reflectance gain. Lower keeps the body in shadow. */
  gain: number
}

/* Per-tier foil treatment. The model ships one grey Foil material, so
   the accent tint is what keeps the tiers distinguishable — on a metal,
   base colour tints the reflection, which is exactly how coloured foil
   behaves. Diamond is the chase product: full spectral split and a
   near-neutral base, so the iridescence is the colour you see. */
const FOIL: Record<string, FoilConfig> = {
  diamond: { iridescence: 1.0, thickness: [260, 860], rough: 0.16, clearcoat: 0.9, tint: 0.06, gain: 0.72 },
  // Gold and silver are the two tiers named after an actual metal, and both
  // want a bright foil — hence the high `gain`. They differ on `tint`, which
  // lerps the base colour toward white: gold keeps its hue (low tint) because
  // the hue is the point, silver takes a high one because a neutral near-white
  // reflectance is what silver *is*. Tinting silver toward its accent instead
  // would just produce a blue-grey pack.
  gold: { iridescence: 0.74, thickness: [235, 760], rough: 0.18, clearcoat: 0.87, tint: 0.07, gain: 0.7 },
  elite: { iridescence: 0.66, thickness: [210, 690], rough: 0.2, clearcoat: 0.83, tint: 0.11, gain: 0.6 },
  silver: { iridescence: 0.5, thickness: [190, 620], rough: 0.22, clearcoat: 0.79, tint: 0.34, gain: 0.64 },
  pro: { iridescence: 0.42, thickness: [165, 555], rough: 0.25, clearcoat: 0.73, tint: 0.15, gain: 0.5 },
  starter: { iridescence: 0.2, thickness: [125, 410], rough: 0.29, clearcoat: 0.66, tint: 0.24, gain: 0.44 },
  _default: { iridescence: 0.38, thickness: [140, 520], rough: 0.3, clearcoat: 0.65, tint: 0.16, gain: 0.42 },
}

/* Accent -> metal base colour. On a metal the base colour *is* the
   reflectance, so feeding the raw accent in gives saturated coloured
   chrome — candy, not foil. Darkening it keeps the body in shadow and
   lets the bright env strips supply the colour as glints. */
function foilColor(accent: THREE.Color, cfg: FoilConfig): THREE.Color {
  const c = accent.clone()
  c.lerp(new THREE.Color(0xffffff), cfg.tint)
  c.multiplyScalar(cfg.gain)
  return c
}

let renderer: THREE.WebGLRenderer | null = null
let camera: THREE.PerspectiveCamera
let scene: THREE.Scene
let envMap: THREE.Texture
let baseMaterialProps: { roughness: number; metalness: number; generatedUV: boolean } | null = null

let root: THREE.Group | null = null // rotated for interaction
let content: THREE.Object3D | null = null // mesh AND Empties, so they move together
let tearMarker: THREE.Object3D | null = null // the Empty the user must drag from
let tearRadius = 0 // its distance from the spin axis, drives the facing test
let lid: THREE.Mesh | null = null // the top strip that tears off
let lidPivot: THREE.Group | null = null // hinge at the seal line, so the lid swings
let lidRest: { y: number } | null = null
let bodyMeshes: THREE.Mesh[] = [] // everything that is neither lid nor card
let card: THREE.Mesh | null = null // the slab that slides out of the mouth
let cardRestY = 0
let cardLift = 0 // travel needed to clear the pack mouth

interface Frame {
  height: number
  width: number
  centerY: number
}
let framePack: Frame | null = null // pack fills the canvas
let frameReveal: Frame | null = null // pack low, headroom for the card

let loadPromise: Promise<boolean> | null = null
let rafId = 0
let lastT = 0

const instances = new Map<HTMLElement, Instance>()

const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches

/* ── environment ──────────────────────────────────────────────
   A dark studio: near-black surround with a few bright emissive panels.
   Foil reads as foil because of the *shape* of what it reflects —
   hard-edged strips give the streaky anisotropic-looking highlights that
   sell it, where a uniform ambient would just make it look like grey
   plastic.                                                            */
function buildEnvironment(gl: THREE.WebGLRenderer): THREE.Texture {
  const env = new THREE.Scene()
  env.background = new THREE.Color(0x06070a)

  const quad = new THREE.PlaneGeometry(1, 1)
  const panel = (
    hex: number,
    gain: number,
    w: number,
    h: number,
    pos: [number, number, number],
    rot: [number, number, number] = [0, 0, 0],
  ) => {
    const m = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide })
    m.color.setHex(hex).multiplyScalar(gain)
    const p = new THREE.Mesh(quad, m)
    p.scale.set(w, h, 1)
    p.position.set(...pos)
    p.rotation.set(...rot)
    env.add(p)
  }

  // key softbox, upper left, raking across the face
  panel(0xffffff, 7.0, 3.2, 5.0, [-4.2, 2.6, 1.6], [0, Math.PI / 2.6, 0.22])
  // narrow specular strips — these become the travelling glints
  panel(0xffffff, 14.0, 0.34, 6.0, [-1.5, 1.2, 4.2], [0, 0, 0.5])
  panel(0xdfe8ff, 9.0, 0.22, 5.0, [2.4, 0.4, 3.4], [0, 0, -0.42])
  // cool rim from behind right, separates the pack from the page
  panel(0x9fc4ff, 5.0, 2.4, 4.4, [4.0, 0.6, -1.8], [0, -Math.PI / 2.4, 0])
  // warm low bounce so the bottom edge doesn't die to pure black
  panel(0xffd9a0, 2.0, 4.0, 1.6, [0.4, -3.0, 2.0], [Math.PI / 2.6, 0, 0])
  // soft top fill
  panel(0xffffff, 1.6, 5.0, 5.0, [0, 5.0, 0], [Math.PI / 2, 0, 0])

  const pmrem = new THREE.PMREMGenerator(gl)
  const rt = pmrem.fromScene(env, 0.04)
  pmrem.dispose()
  quad.dispose()
  env.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.material) (mesh.material as THREE.Material).dispose()
  })
  return rt.texture
}

/* Planar XY projection, used only when the model has no UVs at all. */
function ensureUV(geo: THREE.BufferGeometry): boolean {
  if (geo.attributes.uv) return false
  const pos = geo.attributes.position!
  geo.computeBoundingBox()
  const bb = geo.boundingBox!
  const sx = bb.max.x - bb.min.x || 1
  const sy = bb.max.y - bb.min.y || 1
  const uv = new Float32Array(pos.count * 2)
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = (pos.getX(i) - bb.min.x) / sx
    uv[i * 2 + 1] = (pos.getY(i) - bb.min.y) / sy
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  return true
}

/* ── printed-wrapper projection ──────────────────────────────────
   The authored TEXCOORD_0 unwraps the pack across the whole sheet, which is
   right for a texture painted against *that* layout. Pack artwork is not: it
   is a flat full-bleed design of the printed face, the same thing that gets
   printed on a real wrapper before it is folded.

   So artwork gets its own attribute — a planar projection down Z, measured
   across the whole pack including the lid so the body and the crimped top
   share one continuous print. The front face maps 1:1, the back takes a
   mirrored copy (which is what a real wrapper does), and the ~1mm rounded
   edges smear, which at any size this renders at is invisible.

   `uv1` rather than replacing `uv`, so the base foil texture keeps working
   and three.js can pick between them per-texture via `texture.channel`. */
function projectWrapperUV(geo: THREE.BufferGeometry, box: THREE.Box3, matrix: THREE.Matrix4): void {
  const pos = geo.attributes.position!
  const sx = box.max.x - box.min.x || 1
  const sy = box.max.y - box.min.y || 1
  const uv = new Float32Array(pos.count * 2)
  const v = new THREE.Vector3()

  for (let i = 0; i < pos.count; i++) {
    // Into the shared frame first: the lid is a separate node with its own
    // transform, and projecting it in local space would print it at the wrong
    // scale and offset from the body.
    v.fromBufferAttribute(pos, i).applyMatrix4(matrix)
    uv[i * 2] = (v.x - box.min.x) / sx
    // Flipped, because glTF puts the texture origin at the top left and this
    // projection is measured from the bottom.
    uv[i * 2 + 1] = 1 - (v.y - box.min.y) / sy
  }
  geo.setAttribute('uv1', new THREE.BufferAttribute(uv, 2))
}

/* Pin every vertex of an unwrapped mesh to one texel of background, so it
   takes a single flat colour instead of a smear of the print. */
function flatUV(geo: THREE.BufferGeometry): boolean {
  if (geo.attributes.uv) return false
  const count = geo.attributes.position!.count
  const uv = new Float32Array(count * 2)
  for (let i = 0; i < count; i++) {
    uv[i * 2] = FLAT_UV[0]
    uv[i * 2 + 1] = FLAT_UV[1]
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  return true
}

/* Find the Empty to tear from. Anything in the exported scene that is not
   a mesh/light/camera is a candidate — that is what an Empty comes through
   as. Pivots we added ourselves are flagged and skipped. */
function findTearMarker(rootObj: THREE.Object3D): THREE.Object3D | null {
  const empties: THREE.Object3D[] = []
  rootObj.traverse((o) => {
    if (o === rootObj) return
    const any = o as THREE.Mesh & THREE.Light & THREE.Camera
    if (any.isMesh || any.isLight || any.isCamera) return
    if (o.userData.internal) return
    empties.push(o)
  })
  return empties.find((o) => TEAR_NAME.test(o.name)) ?? null
}

/* The card back, mapped onto the card mesh's UVs.

   The artwork itself lives in `lib/card-back.ts`, shared with the DOM card that
   takes over the moment the slab clears the mouth. That sharing is the point:
   the handoff works by putting both objects in the same place at the same
   instant, and a viewer who notices the picture change has seen the seam it
   exists to hide. They used to be different drawings entirely.

   flipY:false because glTF UVs put the origin at the top left, the opposite of
   three.js's default. */
function cardBackTexture(): THREE.CanvasTexture {
  const { canvas, complete } = createCardBackCanvas()

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.flipY = false
  // Sample the planar projection built for the card, not the authored sheet
  // unwrap — same channel the pack wrapper uses, for the same reason.
  tex.channel = 1

  // The base is painted synchronously; the mark arrives a decode later. Flag
  // the upload then, and wake the loop, or the card keeps the first frame.
  void complete.then(() => {
    tex.needsUpdate = true
    wake()
  })

  return tex
}

async function boot(): Promise<boolean> {
  const gl = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  })
  gl.setClearAlpha(0)
  gl.outputColorSpace = THREE.SRGBColorSpace
  gl.toneMapping = THREE.ACESFilmicToneMapping
  gl.toneMappingExposure = 0.95
  renderer = gl

  envMap = buildEnvironment(gl)

  const gltf = await new GLTFLoader().loadAsync(MODEL_URL)

  /* The model is split into the pack body, the top strip that tears off,
     and the card, so collect every mesh rather than assuming one. */
  const meshes: THREE.Mesh[] = []
  gltf.scene.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh)
  })
  if (!meshes.length) throw new Error(`pack3d: no mesh in ${MODEL_URL}`)

  /* The body arrives unwrapped from Blender; the crimped lid does not.
     Projecting a box onto the lid would drag the printed panel across the
     crimp, so the odd mesh out is pinned to a flat patch of background
     instead — it reads as plain foil, which is what a crimp is. Only a
     model with no UVs at all still gets the projection. */
  const authored = meshes.some((o) => o.geometry.attributes.uv)
  let generatedUV = false
  for (const o of meshes) {
    if (authored) flatUV(o.geometry)
    else if (ensureUV(o.geometry)) generatedUV = true
    if (!o.geometry.attributes.normal) o.geometry.computeVertexNormals()
  }

  const m = meshes[0]!.material as THREE.MeshStandardMaterial
  baseMaterialProps = {
    roughness: m.roughness ?? 0.3,
    metalness: m.metalness ?? 1.0,
    generatedUV,
  }

  scene = new THREE.Scene()
  scene.environment = envMap

  /* root rotates; content carries the mesh and every Empty exported
     alongside it. The geometry is deliberately NOT re-centred — that
     would slide the mesh out from under the Empties and the tear handle
     would no longer sit on the tab. Instead the whole content group is
     offset so the pack still spins about its own centre. */
  root = new THREE.Group()
  content = gltf.scene
  root.add(content)
  scene.add(root)

  /* Split the meshes into body, lid and card. The lid is hung off a pivot
     placed at its bottom edge so it swings open on the seal line like a
     real flap, instead of spinning about its middle. Do this before the
     content offset below — these are local coordinates. */
  card = meshes.find((o) => CARD_NAME.test(o.name)) ?? null
  lid = meshes.find((o) => o !== card && LID_NAME.test(o.name)) ?? null
  bodyMeshes = meshes.filter((o) => o !== lid && o !== card)

  if (card) {
    /* The card ships with no material, so it would otherwise render as
       default grey. It does carry TEXCOORD_0, so paint the back onto it.

       Matte to the point of being nearly unlit: the DOM card this hands over to
       is a flat image with no lighting at all, and any specular response here
       shows up as the slab being visibly brighter than the thing that replaces
       it. Printed card stock is not shiny anyway — the environment is doing
       almost nothing for this surface except giving away the swap. */
    card.material = new THREE.MeshStandardMaterial({
      map: cardBackTexture(),
      roughness: 0.94,
      metalness: 0,
      envMapIntensity: 0.18,
    })
    card.visible = false
    cardRestY = card.position.y
  }

  if (lid) {
    lid.geometry.computeBoundingBox()
    const hingeY = lid.position.y + lid.geometry.boundingBox!.min.y
    lidPivot = new THREE.Group()
    lidPivot.userData.internal = true // not a Blender Empty; keep it out of the marker scan
    lidPivot.position.set(0, hingeY, 0)
    lid.parent!.add(lidPivot)
    lidPivot.add(lid)
    lid.position.y -= hingeY
    lidRest = { y: lidPivot.position.y }
  }

  // frame on everything, lid included, so the top is never clipped
  const meshBox = new THREE.Box3().setFromObject(content)

  /* Wrapper UVs are measured over the body and lid together, before the
     content offset below moves everything — the projection has to describe
     the printed sheet, not where the pack happens to sit in the scene. The
     card is excluded: it is not part of the wrapper. */
  const printBox = new THREE.Box3()
  for (const mesh of meshes) {
    if (mesh === card) continue
    printBox.expandByObject(mesh)
  }
  for (const mesh of meshes) {
    if (mesh === card) continue
    mesh.updateWorldMatrix(true, false)
    projectWrapperUV(mesh.geometry, printBox, mesh.matrixWorld)
  }

  /* The card gets the same planar treatment, over its own bounds.
     Its authored TEXCOORD_0 does not lay the back artwork across the face — it
     shows a zoomed crop of it, which is why the hatch came out coarse and the
     centred mark did not appear at all: the middle of the image was outside the
     region the face samples. Projecting down Z over the card's own box puts the
     whole picture on the face at 1:1, which is the only way it can match the
     DOM card it hands over to. */
  if (card) {
    card.updateWorldMatrix(true, false)
    projectWrapperUV(card.geometry, new THREE.Box3().setFromObject(card), card.matrixWorld)
  }

  content.position.sub(meshBox.getCenter(new THREE.Vector3()))

  tearMarker = findTearMarker(content)
  if (tearMarker) {
    // measured with root un-rotated, so this is the marker's orbit radius
    // about the spin axis — the scale for the facing threshold
    content.updateMatrixWorld(true)
    const tp = tearMarker.getWorldPosition(new THREE.Vector3())
    tearRadius = Math.hypot(tp.x, tp.z)
  }

  // A little direct light on top of the IBL: the env supplies the
  // reflections, these give the edges definition and a hot glint.
  const key = new THREE.DirectionalLight(0xffffff, 2.4)
  key.position.set(-2.2, 2.6, 3.2)
  const rim = new THREE.DirectionalLight(0xbcd4ff, 1.6)
  rim.position.set(2.6, 0.8, -2.4)
  scene.add(key, rim)

  camera = new THREE.PerspectiveCamera(26, 0.703, 0.1, 100)

  const size = meshBox.getSize(new THREE.Vector3())
  // frame on height; the well is taller than it is wide and the pack is
  // narrower still, so height binds. The margin has to cover more than the
  // yaw swing: every instance rests at a baseline forward pitch (see
  // `inst.pitch = 0.12` at mount) and tilts further on hover/drag, and
  // tilting on the X axis grows the model's apparent height, not just its
  // footprint the way a yaw spin does. 1.22 is the smallest margin that
  // keeps the resting tilt clear of the top edge instead of just the spin.
  framePack = { height: size.y * 1.22, width: size.x * 1.22, centerY: 0 }

  /* Reveal framing. The card can only be drawn inside the pack's own
     canvas, so for it to slide clear the canvas has to cover the whole
     well and the camera has to pull back: pack low in frame, empty
     headroom above for the card to travel into. */
  if (card && bodyMeshes[0]) {
    content.updateMatrixWorld(true)
    const bodyBox = new THREE.Box3().setFromObject(bodyMeshes[0])
    const cardBox = new THREE.Box3().setFromObject(card)
    cardLift = (bodyBox.max.y - cardBox.min.y) * 0.97 // bottom edge stops at the mouth
    const top = cardBox.max.y + cardLift
    const bottom = bodyBox.min.y
    frameReveal = {
      height: (top - bottom) * 1.06,
      width: size.x * 1.06,
      centerY: (top + bottom) / 2,
    }
  }
  frameReveal = frameReveal ?? framePack
  return true
}

/* Fit on whichever axis binds. The reveal canvas is tall and narrow, so
   framing on height alone would crop the pack's sides. */
function applyFrame(f: Frame, aspect: number): void {
  const h = Math.max(f.height, (f.width || 0) / aspect)
  const d = h / 2 / Math.tan(((26 * Math.PI) / 180) / 2)
  camera.position.set(0, f.centerY, d)
  camera.lookAt(0, f.centerY, 0)
}

/* The framing is interpolated rather than switched, so the pack visibly
   settles back and down into its reveal position instead of cutting to it.
   Together with the pack element keeping the same DOM box across both
   stages, this is what makes the tear→reveal handover continuous. */
function frameFor(inst: Instance, now: number): Frame {
  const to = inst.frame === 'reveal' ? frameReveal! : framePack!
  if (!inst.frameMs || !inst.frameAt) return to
  const t = Math.min(1, (now - inst.frameAt) / inst.frameMs)
  if (t >= 1) return to
  const from = inst.framePrev === 'reveal' ? frameReveal! : framePack!
  const e = 1 - Math.pow(1 - t, 3)
  return {
    height: from.height + (to.height - from.height) * e,
    width: from.width + (to.width - from.width) * e,
    centerY: from.centerY + (to.centerY - from.centerY) * e,
  }
}

/* One decoded texture per URL. Every configuration of a tier shares one
   wrapper image — the card slider changes the price and the contents, not the
   print — so a grid showing the same tier twice decodes it once. */
const artCache = new Map<string, Promise<THREE.Texture>>()
const WHITE = new THREE.Color(0xffffff)

function loadArt(url: string): Promise<THREE.Texture> {
  const hit = artCache.get(url)
  if (hit) return hit

  const pending = new THREE.TextureLoader().loadAsync(url).then((tex) => {
    tex.colorSpace = THREE.SRGBColorSpace
    // The projection in projectWrapperUV already flips V, so leave the loader's
    // own flip off — doing both would print the wrapper upside down.
    tex.flipY = false
    // The print is read at a raking angle across the pack's curve, which is
    // exactly where isotropic filtering turns fine line work to mush.
    tex.anisotropy = renderer?.capabilities.getMaxAnisotropy() ?? 1
    // Sample the planar projection, not the authored sheet unwrap.
    tex.channel = 1
    return tex
  })

  artCache.set(url, pending)
  return pending
}

/* Dress one instance in its wrapper.

   Foil is fully metallic, where base colour tints the reflection rather than
   painting a surface — so a print applied at full metalness reads as coloured
   chrome, not as ink. Metalness comes down and the base colour goes most of
   the way to white, which lets the artwork read while keeping a trace of the
   tier's hue. Everything that distinguishes the tiers on an unprinted pack —
   iridescence, clearcoat — is left exactly as it was. */
async function applyArt(inst: Instance, url: string): Promise<void> {
  try {
    const tex = await loadArt(url)
    // The instance may have been unmounted, or re-dressed, while we waited.
    if (inst.art !== url || !instances.has(inst.host)) return

    for (const material of [inst.material, inst.lidMaterial]) {
      material.map = tex
      // Computed from the stored foil colour, never from the material's current
      // one — otherwise re-dressing a pack walks the tint further toward white
      // every time.
      material.color.copy(inst.foilColor).lerp(WHITE, 0.86)
      material.metalness = 0.5
      material.roughness = 0.38
      material.envMapIntensity = 1.25
      material.needsUpdate = true
    }
    inst.dirty = true
    wake()
  } catch {
    // No artwork is a worse pack, not a broken one — the tinted foil stands.
  }
}

function materialFor(tier: string, accent: THREE.Color): THREE.MeshPhysicalMaterial {
  const cfg = FOIL[tier] ?? FOIL._default!
  return new THREE.MeshPhysicalMaterial({
    color: foilColor(accent, cfg),
    metalness: baseMaterialProps!.metalness,
    roughness: cfg.rough ?? baseMaterialProps!.roughness,
    side: THREE.DoubleSide,
    envMapIntensity: 1.1,
    clearcoat: cfg.clearcoat,
    clearcoatRoughness: 0.14,
    iridescence: cfg.iridescence,
    iridescenceIOR: 1.32,
    iridescenceThicknessRange: cfg.thickness,
  })
}

export interface MountOptions {
  /** Drives the foil tint. */
  accent: string
  /** Drives the foil treatment preset. */
  tier: string
  /** Printed wrapper artwork, applied to the planar `uv1` projection. */
  art?: string | null
  /** Show the pulsing tear hotspot and allow the tab to be grabbed. */
  tearable?: boolean
  /** Slow idle spin while nothing else is happening. */
  idle?: boolean
}

/* ── one pack ─────────────────────────────────────────────── */
class Instance {
  readonly canvas: HTMLCanvasElement
  readonly ctx: CanvasRenderingContext2D
  material: THREE.MeshPhysicalMaterial
  lidMaterial: THREE.MeshPhysicalMaterial

  tear = 0 // 0..1 peel progress while dragging the tab
  tearOffAt = 0 // timestamp the lid started coming off
  frame: 'pack' | 'reveal' = 'pack'
  framePrev: 'pack' | 'reveal' = 'pack'
  frameAt = 0
  frameMs = 0
  cardOutAt = 0
  cardVisible = false

  readonly tier: string

  /** Where this pack comes back to. Set from `tearable` in the constructor. */
  restYaw = REST_YAW

  yaw = REST_YAW
  pitch = 0.12
  vYaw = 0
  vPitch = 0
  w = 0
  h = 0
  dirty = true
  visible = true
  hover = false
  dragging = false
  tearable: boolean
  idle: boolean
  art: string | null
  /** The unprinted foil colour, kept so dressing the pack is idempotent. */
  readonly foilColor: THREE.Color

  private ro: ResizeObserver

  constructor(
    readonly host: HTMLElement,
    opts: MountOptions,
  ) {
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'pack3d-canvas'
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
    this.ctx = this.canvas.getContext('2d')!
    host.appendChild(this.canvas)

    this.tier = opts.tier

    this.material = materialFor(opts.tier, new THREE.Color(opts.accent))
    this.foilColor = this.material.color.clone()
    // the lid fades out as it flies away, so it needs a material of its
    // own — fading the shared one would dissolve the whole pack
    this.lidMaterial = this.material.clone()
    this.tearable = opts.tearable ?? false
    // After `tearable`, because it depends on it — field initialisers have
    // already run by this point, so `yaw` has to be re-seeded here too.
    this.restYaw = this.tearable ? TEAR_REST_YAW : REST_YAW
    this.yaw = this.restYaw
    this.idle = opts.idle ?? true
    this.art = opts.art ?? null
    if (this.art) void applyArt(this, this.art)

    this.ro = new ResizeObserver(() => {
      this.measure()
      this.dirty = true
      wake()
    })
    this.ro.observe(host)
    this.measure()
    io.observe(host)
  }

  measure(): void {
    const r = this.host.getBoundingClientRect()
    const dpr = Math.min(devicePixelRatio || 1, MAX_DPR)
    const w = Math.max(1, Math.round(r.width * dpr))
    const h = Math.max(1, Math.round(r.height * dpr))
    if (w === this.w && h === this.h) return
    this.w = w
    this.h = h
    this.canvas.width = w
    this.canvas.height = h
  }

  step(dt: number): boolean {
    if (this.dragging) return true

    // The hotspot pulses, so a tearable pack has to keep drawing — report
    // motion, or the loop parks and the pulse freezes after one frame.
    let moving = Boolean(this.tearable && tearMarker)
    const now = performance.now()

    // keep rendering while the lid is coming off, or a card is sliding
    if (this.tearOffAt && now - this.tearOffAt < TEAR_OFF_MS + 60) moving = true
    if (this.cardOutAt && now - this.cardOutAt < CARD_OUT_MS + 60) moving = true
    if (this.frameMs && now - this.frameAt < this.frameMs + 60) moving = true

    // inertia
    if (Math.abs(this.vYaw) > 1e-4 || Math.abs(this.vPitch) > 1e-4) {
      this.yaw += this.vYaw * dt
      this.pitch += this.vPitch * dt
      const damp = Math.pow(0.0022, dt)
      this.vYaw *= damp
      this.vPitch *= damp
      moving = true
    }

    // gentle idle drift while hovered (desktop affordance that it's grabbable)
    if (this.hover && this.idle && !reduceMotion()) {
      this.yaw += 0.35 * dt
      moving = true
    } else if (!this.dragging && Math.abs(this.vYaw) < SETTLE_BELOW) {
      // Nothing is driving the pack any more, so bring it back to front.
      if (this.settleYaw(dt)) moving = true
    }

    // pitch self-levels so a pack can never end up stuck on its head
    const lvl = -this.pitch * Math.min(1, dt * 3.2)
    if (Math.abs(this.pitch) > 1e-3) {
      this.pitch += lvl
      moving = true
    }
    this.pitch = Math.max(-0.6, Math.min(0.6, this.pitch))
    return moving
  }

  /**
   * Eases the yaw back to rest once a throw has run out of momentum.
   *
   * An exponential approach rather than a spring: a spring overshoots, and a
   * pack that swings past square and back again reads as wobbly rather than as
   * settling into place.
   *
   * The delta is taken the short way round, so a pack spun through six turns
   * unwinds by at most half a turn instead of rewinding every one of them —
   * and the snap at the end folds those whole turns away, which is also what
   * stops `yaw` growing without bound across a long session.
   */
  private settleYaw(dt: number): boolean {
    const TAU = Math.PI * 2
    let delta = (this.restYaw - this.yaw) % TAU
    if (delta > Math.PI) delta -= TAU
    if (delta < -Math.PI) delta += TAU

    if (Math.abs(delta) < 0.001) {
      if (this.yaw === this.restYaw) return false
      this.yaw = this.restYaw
      return true
    }

    // Reduced motion gets the correction without the journey.
    this.yaw += delta * (reduceMotion() ? 1 : 1 - Math.pow(SETTLE_RETAIN, dt))
    return true
  }

  dispose(): void {
    this.ro.disconnect()
    io.unobserve(this.host)
    this.material.dispose()
    this.lidMaterial.dispose()
    this.canvas.remove()
  }
}

const io =
  typeof IntersectionObserver === 'undefined'
    ? ({ observe() {}, unobserve() {} } as unknown as IntersectionObserver)
    : new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            const inst = instances.get(e.target as HTMLElement)
            if (!inst) continue
            inst.visible = e.isIntersecting
            if (e.isIntersecting) {
              inst.dirty = true
              wake()
            }
          }
        },
        { rootMargin: '120px' },
      )

/* Project the tear Empty to the instance's canvas, and decide whether it
   is reachable. The camera sits on +Z, so world z tells us which half of
   the spin the marker is on.

   The threshold is NOT z > 0. This Empty sits on the pack's left edge at
   z = 0, so it rides the silhouette: at a face-on view it lands exactly on
   the mid-plane and a strict test would flicker the tab off at the most
   natural viewing angle. Allowing it slightly behind centre — a fraction
   of its own orbit radius — keeps it grabbable through the whole front-ish
   arc and only drops it once it has genuinely swung round the back. */
const _v = new THREE.Vector3()
function tabProjection(inst: Instance): { nx: number; ny: number; front: boolean } | null {
  if (!tearMarker || !root) return null
  root.rotation.set(inst.pitch, inst.yaw, 0)
  root.updateMatrixWorld(true)
  tearMarker.getWorldPosition(_v)
  const front = _v.z > -0.4 * tearRadius
  _v.project(camera)
  return { nx: _v.x * 0.5 + 0.5, ny: -_v.y * 0.5 + 0.5, front }
}

/* Ring drawn over the blit, marking where the tab is. Without it the pack
   gives no clue which spot opens it — and since the pack rotates, that
   spot moves. Hidden when the tab spins round the back. */
function drawTearHotspot(inst: Instance): void {
  const p = tabProjection(inst)
  if (!p || !p.front) return
  const rect = inst.host.getBoundingClientRect()
  const scale = rect.width ? inst.w / rect.width : 1
  const x = p.nx * inst.w
  const y = p.ny * inst.h
  const pulse = 0.5 + 0.5 * Math.sin((performance.now() / 1000) * 3)
  const r = (11 + pulse * 3) * scale
  const ctx = inst.ctx
  ctx.save()
  ctx.lineWidth = 1.6 * scale
  ctx.strokeStyle = `rgba(48,248,16,${(0.55 + pulse * 0.35).toFixed(3)})`
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = `rgba(48,248,16,${(0.18 + pulse * 0.12).toFixed(3)})`
  ctx.beginPath()
  ctx.arc(x, y, r * 0.42, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

/* Is this pointer on the tear tab? Radius has a 22px floor so it stays a
   usable touch target on the smallest pack. */
function hitTab(inst: Instance, clientX: number, clientY: number): boolean {
  const p = tabProjection(inst)
  if (!p || !p.front) return false
  const r = inst.host.getBoundingClientRect()
  const x = r.left + p.nx * r.width
  const y = r.top + p.ny * r.height
  const radius = Math.max(22, Math.min(r.width, r.height) * 0.18)
  return Math.hypot(clientX - x, clientY - y) <= radius
}

/* Pose the lid for this instance. The scene is shared, so every render has
   to set this explicitly — otherwise a pack that never tears would inherit
   the last torn pack's open flap.

   Two phases: while the tab is being dragged the lid peels back a little
   on its hinge, and once the tear fires it swings wide, lifts off in the
   direction of the drag and fades. */
function poseLid(inst: Instance, now: number): void {
  if (!lidPivot || !lid || !lidRest) return
  const off = inst.tearOffAt ? Math.min(1, (now - inst.tearOffAt) / TEAR_OFF_MS) : 0
  const ease = 1 - Math.pow(1 - off, 3)
  const peel = inst.tear

  lidPivot.rotation.set(0.45 * peel + 1.15 * ease, 0, 0.55 * ease)
  lidPivot.position.set(0.2 * ease, lidRest.y + 0.015 * peel + 0.1 * ease, 0.06 * ease)
  lidPivot.scale.setScalar(1)

  // Hold full opacity while the flap is still readable, then dissolve. The
  // ease-out front-loads the travel, so fading much earlier than this makes
  // the lid vanish before the motion registers.
  const fade = Math.max(0, (ease - 0.62) / 0.38)
  inst.lidMaterial.opacity = 1 - fade
  inst.lidMaterial.transparent = fade > 0
  lid.visible = fade < 1
}

/* Slide the card for this instance. Like the lid, the scene is shared, so
   this is set every render — a pack that is not revealing must have its
   card stowed and hidden, or it would inherit the last one's. */
function poseCard(inst: Instance, now: number): void {
  if (!card) return
  if (!inst.cardOutAt) {
    card.visible = false
    return
  }
  const t = Math.min(1, (now - inst.cardOutAt) / CARD_OUT_MS)
  const ease = 1 - Math.pow(1 - t, 3)
  card.visible = inst.cardVisible
  card.position.y = cardRestY + cardLift * ease
}

/* Where the card is on screen, as a client-space rect. The DOM card is
   snapped onto this for the handoff, so the swap from mesh to DOM lands in
   exactly the same place. */
function cardRectFor(inst: Instance): DOMRectLike | null {
  if (!card || !inst.cardOutAt || !root) return null
  root.rotation.set(inst.pitch, inst.yaw, 0)
  poseCard(inst, performance.now())
  root.updateMatrixWorld(true)
  card.geometry.computeBoundingBox()
  const bb = card.geometry.boundingBox!
  const host = inst.host.getBoundingClientRect()
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (let i = 0; i < 8; i++) {
    _v.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z)
    card.localToWorld(_v).project(camera)
    const x = host.left + (_v.x * 0.5 + 0.5) * host.width
    const y = host.top + (-_v.y * 0.5 + 0.5) * host.height
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY }
}

export interface DOMRectLike {
  left: number
  top: number
  width: number
  height: number
}

function renderInstance(inst: Instance): void {
  if (!inst.w || !inst.h || !renderer || !root) return
  const now = performance.now()
  renderer.setSize(inst.w, inst.h, false)
  applyFrame(frameFor(inst, now), inst.w / inst.h)
  camera.aspect = inst.w / inst.h
  camera.updateProjectionMatrix()
  for (const b of bodyMeshes) b.material = inst.material
  if (lid) lid.material = inst.lidMaterial
  poseLid(inst, now)
  poseCard(inst, now)
  root.rotation.set(inst.pitch, inst.yaw, 0)
  renderer.render(scene, camera)
  inst.ctx.clearRect(0, 0, inst.w, inst.h)
  inst.ctx.drawImage(renderer.domElement, 0, 0, inst.w, inst.h)
  if (inst.tearable && tearMarker) drawTearHotspot(inst)
}

function tick(t: number): void {
  rafId = 0
  const dt = Math.min(0.05, (t - lastT) / 1000 || 0.016)
  lastT = t
  let busy = false
  for (const inst of instances.values()) {
    if (!inst.visible) continue
    const moving = inst.step(dt)
    if (moving || inst.dirty) {
      renderInstance(inst)
      inst.dirty = false
    }
    if (moving) busy = true
  }
  if (busy) wake()
}

function wake(): void {
  if (!rafId && renderer) {
    lastT = performance.now()
    rafId = requestAnimationFrame(tick)
  }
}

/* ── gestures ─────────────────────────────────────────────────
   Delegated from the document, because the pack canvas is not always the
   top-most element. A drag only becomes a rotation after DRAG_MIN px, so
   a click on a pack tile still reaches the link underneath; over that
   threshold the click is swallowed so rotating never also navigates. */
const DRAG_MIN = 6
let drag: {
  inst: Instance
  x: number
  y: number
  px: number
  py: number
  engaged: boolean
} | null = null

/**
 * Ends the current drag, however it ended.
 *
 * The one place `drag` is torn down, because the ways a drag can finish are
 * not one way: a release, a gesture the browser took over, or a release the
 * page never heard about. Each of those has to leave the pack in the same
 * state — free to settle — and clearing `dragging` is the part that lets
 * `step` start levelling the pitch and easing the yaw home again.
 */
function endDrag(): void {
  if (!drag) return
  const { inst, engaged } = drag
  inst.dragging = false
  // Only a drag that actually engaged should eat the trailing click; a press
  // that never moved is a click and belongs to whatever is underneath.
  if (engaged) suppressClickUntil = performance.now() + 400
  drag = null
  wake()
}

function instanceFromEvent(e: PointerEvent): Instance | null {
  const target = e.target as Element | null
  if (!target?.closest) return null
  const el = target.closest<HTMLElement>('[data-pack3d]')
  return el ? (instances.get(el) ?? null) : null
}

let listenersBound = false
function bindGestures(): void {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true

  addEventListener(
    'pointerdown',
    (e) => {
      if (e.button != null && e.button !== 0) return
      // Always clear first. A drag that began on a pack whose host was removed
      // mid-gesture (the rip theatre closing, say) never sees its pointerup, and
      // a stale `drag` left here goes on preventing defaults and swallowing the
      // click after every pointerup — anywhere on the site, forever.
      drag = null
      const inst = instanceFromEvent(e)
      if (!inst) return
      // Starting on the tear tab is a rip, not a rotate — yield the drag to
      // the rip flow and do not spin the pack.
      if (inst.tearable && tearMarker && hitTab(inst, e.clientX, e.clientY)) return
      drag = { inst, x: e.clientX, y: e.clientY, px: e.clientX, py: e.clientY, engaged: false }
    },
    true,
  )

  addEventListener(
    'pointermove',
    (e) => {
      if (!drag) return
      if (!instances.has(drag.inst.host)) {
        drag = null
        return
      }
      /* A drag whose end we never saw. Two ways that happens and neither is
         exotic: the pack canvas sets `touch-action: pan-y`, so the browser is
         entitled to take a vertical gesture away mid-drag — and when it does
         it sends `pointercancel`, not `pointerup` — and a button released
         outside the window delivers no `pointerup` to the page at all.

         Either way the next move arrives with nothing held, and without this
         the stale drag keeps steering the pack on every subsequent pointer
         move. Downward is the direction a vertical pan travels, so what that
         looks like is a pack that will not stop tipping over. It cannot
         recover on its own either: `step` returns early while `dragging` is
         set, so the pitch never levels and the yaw never settles. */
      if (e.buttons === 0) {
        endDrag()
        return
      }
      const dx = e.clientX - drag.px
      const dy = e.clientY - drag.py
      drag.px = e.clientX
      drag.py = e.clientY

      if (!drag.engaged) {
        if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < DRAG_MIN) return
        drag.engaged = true
        drag.inst.dragging = true
      }
      e.preventDefault()
      const inst = drag.inst
      inst.yaw += dx * 0.011
      inst.pitch += dy * 0.006
      inst.pitch = Math.max(-0.6, Math.min(0.6, inst.pitch))
      inst.vYaw = (dx * 0.011) / 0.016
      inst.vPitch = 0
      inst.dirty = true
      wake()
    },
    { capture: true, passive: false },
  )

  /* Time-boxed rather than a once-only listener: a drag does not always
     produce a trailing click (pointer released off-target, or a touch drag
     that never synthesises one). A pending once-listener would then sit
     armed indefinitely and swallow the next genuine click on the page. */
  addEventListener(
    'click',
    (e) => {
      if (performance.now() > suppressClickUntil) return
      suppressClickUntil = 0
      e.stopPropagation()
      e.preventDefault()
    },
    true,
  )

  addEventListener('pointerup', endDrag, true)
  /* `pointercancel` is not an edge case here: it is what the browser sends
     whenever it claims a gesture the page had started, which `touch-action:
     pan-y` on the pack canvas invites it to do on every vertical swipe.
     Treated exactly like an up, because for our purposes it is one. */
  addEventListener('pointercancel', endDrag, true)

  addEventListener(
    'pointerover',
    (e) => {
      const inst = instanceFromEvent(e)
      if (inst && !inst.hover) {
        inst.hover = true
        wake()
      }
    },
    true,
  )
  addEventListener(
    'pointerout',
    (e) => {
      const inst = instanceFromEvent(e)
      if (inst) inst.hover = false
    },
    true,
  )
}

let suppressClickUntil = 0

/* ── public surface ───────────────────────────────────────── */

export function start(): Promise<boolean> {
  if (loadPromise) return loadPromise
  loadPromise = boot()
    .then(() => {
      bindGestures()
      return true
    })
    .catch((err: Error) => {
      // No WebGL, or the model failed to load — callers fall back to CSS.
      console.warn('pack3d disabled:', err.message)
      return false
    })
  return loadPromise
}

export const ready = (): Promise<boolean> => start()

/**
 * Attach a pack to a host element. The host must be positioned (the canvas
 * is absolutely filled into it) and must carry `data-pack3d` for the
 * delegated gestures to find it.
 */
export async function mount(host: HTMLElement, opts: MountOptions): Promise<boolean> {
  const ok = await start()
  if (!ok) return false
  if (instances.has(host)) return true
  host.dataset.pack3d = ''
  instances.set(host, new Instance(host, opts))
  wake()
  return true
}

export function unmount(host: HTMLElement): void {
  const inst = instances.get(host)
  if (!inst) return
  // Drop any gesture still pointing at this instance before it goes away.
  if (drag?.inst === inst) drag = null
  inst.dispose()
  instances.delete(host)
}

/** Swap a mounted pack's wrapper artwork. */
export function setPackArt(host: HTMLElement, url: string | null): void {
  const inst = instances.get(host)
  if (!inst || inst.art === url) return
  inst.art = url
  if (url) void applyArt(inst, url)
}

/** Warm the cache before a pack is mounted, so tiles do not pop in ungraded. */
export function preloadArt(url: string): void {
  void start().then((ok) => {
    if (ok) void loadArt(url).catch(() => undefined)
  })
}

export const hasTearTab = (): boolean => Boolean(tearMarker)
export const hasLid = (): boolean => Boolean(lid)
export const hasCard = (): boolean => Boolean(card)
export const usesGeneratedUVs = (): boolean => Boolean(baseMaterialProps?.generatedUV)

export function hitTearTab(host: HTMLElement, clientX: number, clientY: number): boolean {
  const inst = tearMarker ? instances.get(host) : null
  return inst ? hitTab(inst, clientX, clientY) : false
}

/** Peel progress, 0..1, driven by how far the tab has been dragged, so the
    pack looks worked open rather than snapping at a threshold. */
export function setTear(host: HTMLElement, t: number): void {
  const inst = instances.get(host)
  if (!inst) return
  inst.tear = Math.max(0, Math.min(1, t || 0))
  inst.dirty = true
  wake()
}

/** Turn the tear hotspot on or off. Switched off once torn: it stops the
    ring pulsing over an already-open pack, and stops that instance
    rendering every frame for the rest of the reveal. */
export function setTearable(host: HTMLElement, on: boolean): void {
  const inst = instances.get(host)
  if (!inst) return
  inst.tearable = on
  inst.dirty = true
  wake()
}

/** Switch between framings. 'reveal' pulls the camera back and drops the
    pack low so there is headroom above it for the card to slide into. */
export function setFrame(host: HTMLElement, name: 'pack' | 'reveal', ms = 0): void {
  const inst = instances.get(host)
  if (!inst) return
  if (name === inst.frame) return
  inst.framePrev = inst.frame
  inst.frame = name
  inst.frameAt = performance.now()
  inst.frameMs = ms
  inst.dirty = true
  wake()
}

/** Fire the lid off. Returns the duration so the caller can line up. */
export function tearOff(host: HTMLElement): number {
  const inst = instances.get(host)
  if (!inst) return 0
  inst.tearOffAt = performance.now()
  inst.dirty = true
  wake()
  return TEAR_OFF_MS
}

/** Slide a card up out of the mouth. Returns the duration. */
export function slideCardOut(host: HTMLElement): number {
  const inst = instances.get(host)
  if (!inst || !card) return 0
  inst.cardOutAt = performance.now()
  inst.cardVisible = true
  inst.dirty = true
  wake()
  return CARD_OUT_MS
}

/** Client-space rect of the card as currently posed, for the DOM handoff. */
export function cardRect(host: HTMLElement): DOMRectLike | null {
  const inst = instances.get(host)
  return inst ? cardRectFor(inst) : null
}

/** Put the card away once the DOM card has taken over. */
export function hideCard(host: HTMLElement): void {
  const inst = instances.get(host)
  if (!inst) return
  inst.cardVisible = false
  inst.cardOutAt = 0
  inst.dirty = true
  wake()
}

/** Reset a pack to sealed — used when the same host opens a second pack. */
export function reset(host: HTMLElement): void {
  const inst = instances.get(host)
  if (!inst) return
  inst.tear = 0
  inst.tearOffAt = 0
  inst.cardOutAt = 0
  inst.cardVisible = false
  inst.frame = 'pack'
  inst.framePrev = 'pack'
  inst.frameAt = 0
  inst.frameMs = 0
  inst.yaw = inst.restYaw
  inst.pitch = 0.12
  inst.vYaw = 0
  inst.dirty = true
  wake()
}
